import React, { FC } from 'react';

import { Box, Flex, HStack, Stack } from '@chakra-ui/react';

import { InformationAlert } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { Select } from 'components/DataEntry';
import {
  DeleteAttributeIconButton,
  ExclamationCircleIcon,
  getIconColor,
  GroupIcon,
  IconTypes,
  PrimaryButton,
  RoleIcon,
  UserIcon,
} from 'components/index';
import { IMenuItemProps, Menu } from 'components/Overlay';
import {
  addConditionButtonStyles,
  conditionsSelectorStyles,
} from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';
import { renderDataAccessFormLabel } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/utils';

interface PermanentAccessItem {
  id: string;
  principalType: 'user' | 'group';
  principal: Record<string, any> | null;
  roles: Record<string, any>[];
}

interface Props {
  userOptions: Record<string, any>[];
  groupOptions: Record<string, any>[];
  roleOptions: Record<string, any>[];
  permanentAccessItems: Record<string, PermanentAccessItem>;
  isUsersLoading: boolean;
  isGroupsLoading: boolean;
  isRolesLoading: boolean;
  onAddItem: (
    principalType: PermanentAccessItem['principalType'],
    principal: Record<string, any>,
  ) => void;
  onRemoveItem: (item: PermanentAccessItem) => void;
  onUpdateItem: (
    item: PermanentAccessItem,
    field: 'principal' | 'roles',
    value: Record<string, any> | Record<string, any>[] | null,
  ) => void;
}

export const PermanentAccess: FC<Props> = ({
  userOptions,
  groupOptions,
  roleOptions,
  permanentAccessItems,
  isUsersLoading,
  isGroupsLoading,
  isRolesLoading,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
}) => {
  const getPrincipalOptions = (item: PermanentAccessItem) => {
    const options = item.principalType === 'group' ? groupOptions : userOptions;
    const selectedPrincipalValues = new Set(
      Object.values(permanentAccessItems)
        .filter(
          otherItem =>
            otherItem.id !== item.id &&
            otherItem.principalType === item.principalType,
        )
        .map(otherItem => otherItem.principal?.value)
        .filter(Boolean)
        .map(value => String(value)),
    );

    const filteredOptions = options.filter(
      option => !selectedPrincipalValues.has(String(option.value)),
    );

    if (
      item.principal &&
      !filteredOptions.some(
        option => String(option.value) === String(item.principal?.value),
      )
    ) {
      return [item.principal, ...filteredOptions];
    }

    return filteredOptions;
  };

  const handleAddItem = (
    principalType: PermanentAccessItem['principalType'],
  ) => {
    const options = principalType === 'group' ? groupOptions : userOptions;
    const selectedPrincipalValues = new Set(
      Object.values(permanentAccessItems)
        .filter(item => item.principalType === principalType)
        .map(item => item.principal?.value)
        .filter(Boolean)
        .map(value => String(value)),
    );
    const defaultPrincipal = options.find(
      option => !selectedPrincipalValues.has(String(option.value)),
    );

    if (defaultPrincipal) onAddItem(principalType, defaultPrincipal);
  };

  return (
    <Stack spacing={5}>
      <InformationAlert
        icon={<ExclamationCircleIcon color="primary" />}
        iconSize={6}
        spacing={2}
        py={3}
        w="80%"
        description="These groups or users always have access to the roles below. No JIT request needed; Roles below always allowed"
      />
      <BorderedBox p={0}>
        <Stack spacing={4}>
          <HStack
            w="full"
            align="center"
            spacing={0}
            borderBottom="1px solid"
            borderColor="componentBorder"
            px={10}
            pt={3}
            pb={2}
            fontWeight={600}
            fontSize="15px"
          >
            <Box w="38%" flexShrink={0} whiteSpace="nowrap">
              {renderDataAccessFormLabel('User or Group', 'full')}
            </Box>
            <Box pl={2} w="48%" flexShrink={0} whiteSpace="nowrap">
              {renderDataAccessFormLabel('Choose Roles', 'full')}
            </Box>

            <Flex flex={1} justify="flex-end">
              <Menu
                menuItems={
                  [
                    {
                      icon: <UserIcon />,
                      key: 'user',
                      label: 'User',
                      onClick: () => handleAddItem('user'),
                    },
                    {
                      icon: <GroupIcon />,
                      key: 'group',
                      label: 'Group',
                      onClick: () => handleAddItem('group'),
                    },
                  ] as IMenuItemProps[]
                }
                buttonLabel={
                  <PrimaryButton {...addConditionButtonStyles}>
                    + ADD
                  </PrimaryButton>
                }
                menuTransition={false}
              />
            </Flex>
          </HStack>

          <Stack px={10} pb={6}>
            {Object.values(permanentAccessItems).map(item => (
              <HStack key={item.id} align="center">
                <Box w="38%">
                  <Select
                    options={getPrincipalOptions(item)}
                    value={item.principal}
                    isMulti={false}
                    isLoading={
                      item.principalType === 'group'
                        ? isGroupsLoading
                        : isUsersLoading
                    }
                    valueContainerIcon={
                      item.principalType === 'group' ? (
                        <GroupIcon />
                      ) : (
                        <UserIcon />
                      )
                    }
                    onChange={value => onUpdateItem(item, 'principal', value)}
                    showClearIndicator={false}
                    styles={conditionsSelectorStyles.select}
                    menuPortalTarget={document.body}
                    placeholder={'Select ' + item.principalType}
                  />
                </Box>
                <Box w="48%">
                  <Select
                    options={roleOptions}
                    value={item.roles}
                    showClearIndicator={false}
                    isMulti
                    isLoading={isRolesLoading}
                    showTotalSelected
                    valueContainerIcon={
                      <RoleIcon color={getIconColor(IconTypes.Snowflake)} />
                    }
                    selectedMessage={
                      'Snowflake role' +
                      (item.roles?.length === 1 ? '' : 's') +
                      ' selected'
                    }
                    onChange={value => onUpdateItem(item, 'roles', value)}
                    styles={conditionsSelectorStyles.select}
                    menuPortalTarget={document.body}
                  />
                </Box>
                <DeleteAttributeIconButton onClick={() => onRemoveItem(item)} />
              </HStack>
            ))}
          </Stack>
        </Stack>
      </BorderedBox>
    </Stack>
  );
};
